import { Alert, Button, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import CustomModal from "../../../../ui/components/custom-modal/custom-modal";
import { ConvenioPractica } from "../../../models/convenio-practica";
import { convenioServices } from "../../../services/convenio.services";
import "./convenio-practica-edit-modal.scss";

interface EditValues {
  numeroPractica: string;
  descripcion: string;
  tipo: string;
  precio: string;
  coseguro: string;
  antiguedadMinima: string;
  antiguedadMaxima: string;
}

export interface ConvenioPracticaEditModalProps {
  show: boolean;
  convenioPractica: ConvenioPractica | null;
  consideraAntiguedad: boolean;
  handleClose: (updated?: ConvenioPractica) => void;
}

export default function ConvenioPracticaEditModal({
  show,
  convenioPractica,
  consideraAntiguedad,
  handleClose,
}: ConvenioPracticaEditModalProps) {
  const [values, setValues] = useState<EditValues | null>(null);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (convenioPractica) {
      setValues({
        numeroPractica: convenioPractica.numeroPractica,
        descripcion: convenioPractica.descripcion,
        tipo: convenioPractica.tipo,
        precio: String(convenioPractica.precio),
        coseguro: String(convenioPractica.coseguro),
        antiguedadMinima: String(convenioPractica.antiguedadMinima),
        antiguedadMaxima: String(convenioPractica.antiguedadMaxima),
      });
      setErrorMessage("");
    }
  }, [convenioPractica]);

  const setField = (field: keyof EditValues, value: string) =>
    setValues((prev) => (prev ? { ...prev, [field]: value } : prev));

  const DECIMAL_2_REGEX = /^\d*(\.\d{0,2})?$/;
  const setDecimalField = (field: "precio" | "coseguro", value: string) => {
    if (DECIMAL_2_REGEX.test(value)) setField(field, value);
  };

  const handleSave = () => {
    if (!convenioPractica || !values) return;
    setSaving(true);
    setErrorMessage("");
    const payload = {
      numeroPractica: values.numeroPractica,
      descripcion: values.descripcion,
      tipo: values.tipo,
      precio: Number(values.precio),
      coseguro: Number(values.coseguro),
      antiguedadMinima: Number(values.antiguedadMinima),
      antiguedadMaxima: Number(values.antiguedadMaxima),
    };
    convenioServices
      .updatePractica(convenioPractica.convenioPracticaPrecioId, payload)
      .then(() => handleClose({ ...convenioPractica, ...payload }))
      .catch(() => {
        setErrorMessage("No se pudo guardar la práctica. Intente nuevamente.");
      })
      .finally(() => setSaving(false));
  };

  const Actions = () => (
    <>
      <Button onClick={() => handleClose()} disabled={saving}>
        Cancelar
      </Button>
      <Button variant="contained" onClick={handleSave} disabled={saving}>
        {saving ? "Guardando..." : "Guardar"}
      </Button>
    </>
  );

  return (
    <CustomModal
      show={show}
      handleClose={() => handleClose()}
      title="Editar Práctica"
      actions={<Actions />}
      size="md"
    >
      <div className="convenio-practica-edit-modal">
        {errorMessage && (
          <Alert severity="error" onClose={() => setErrorMessage("")}>
            {errorMessage}
          </Alert>
        )}
        {values && (
          <>
            <div className="row">
              <TextField
                label="Nombre"
                value={values.descripcion}
                onChange={(e) => setField("descripcion", e.target.value)}
                fullWidth
              />
            </div>
            <div className="row">
              <TextField
                label="Nº Práctica"
                value={values.numeroPractica}
                onChange={(e) => setField("numeroPractica", e.target.value)}
                fullWidth
              />
              <TextField
                label="Tipo"
                className="tipo-field"
                value={values.tipo}
                onChange={(e) => setField("tipo", e.target.value)}
              />
            </div>
            {consideraAntiguedad && (
              <div className="row">
                <TextField
                  label="Antigüedad Mínima"
                  type="number"
                  value={values.antiguedadMinima}
                  onChange={(e) =>
                    setField("antiguedadMinima", e.target.value)
                  }
                  fullWidth
                />
                <TextField
                  label="Antigüedad Máxima"
                  type="number"
                  value={values.antiguedadMaxima}
                  onChange={(e) =>
                    setField("antiguedadMaxima", e.target.value)
                  }
                  fullWidth
                />
              </div>
            )}
            <div className="row">
              <TextField
                label="Precio"
                type="text"
                inputMode="decimal"
                value={values.precio}
                onChange={(e) => setDecimalField("precio", e.target.value)}
                fullWidth
              />
              <TextField
                label="Coseguro"
                type="text"
                inputMode="decimal"
                value={values.coseguro}
                onChange={(e) => setDecimalField("coseguro", e.target.value)}
                fullWidth
              />
            </div>
          </>
        )}
      </div>
    </CustomModal>
  );
}
